"use client";

import { useEffect, useState } from "react";
import { Check, LoaderCircle, Minus, Sparkles } from "lucide-react";
import { DESIGN_STAGES, stageState, type DesignProgress as Progress, type DesignStage } from "@/lib/pipeline/progress";

const copy: Record<DesignStage, { title: string; lines: [string, string] }> = {
  selecting: {
    title: "Choosing furniture and colours",
    lines: ["Finding pieces that suit your room and style.", "Bringing the furniture, colours and textures into one scheme."],
  },
  rendering: {
    title: "Creating your room",
    lines: ["Bringing the selected furniture into your room photo.", "Working with your room’s perspective, proportions and natural light."],
  },
  finishing: {
    title: "Adding finishing touches",
    lines: ["Layering in your selected colours, lighting and accessories.", "Carrying the room’s layout and furniture arrangement through to the finished look."],
  },
  saving: {
    title: "Saving your room and shopping list",
    lines: ["Saving your finished design and gathering the product links.", "Getting your private room link ready to revisit."],
  },
};

export function DesignProgress({ progress, startedAt, hasSavedRoom }: {
  progress?: Progress;
  startedAt?: string;
  hasSavedRoom: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const start = startedAt ? Date.parse(startedAt) : NaN;
  const elapsed = Number.isFinite(start) ? Math.max(0, Math.floor((now - start) / 1000)) : 0;
  const complete = progress?.stage === "complete";
  const active = DESIGN_STAGES.find(stage => stage === progress?.stage);
  const status = complete ? "Your room is ready" : active ? copy[active].title : hasSavedRoom
    ? "Getting your room design underway" : "Preparing your room";

  return (
    <section aria-label="Room design progress" className="py-12 sm:py-20 max-w-lg mx-auto space-y-7 animate-in fade-in duration-300">
      <div className="text-center space-y-3">
        <div className="relative w-16 h-16 mx-auto mb-5" aria-hidden="true">
          <div className={`absolute inset-0 rounded-full border-2 border-line ${complete ? "border-sage" : "border-t-clay motion-safe:animate-spin"}`} />
          <div className="absolute inset-2 rounded-full bg-paper flex items-center justify-center text-clay">
            {complete ? <Check className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
          </div>
        </div>
        <h2 className="text-2xl font-bold text-ink">Reimagining your space</h2>
        <p className="text-sm text-ink-soft">This usually takes around 1–2 minutes.</p>
        <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">{status}</p>
        {!active && !complete && <p className="text-sm text-ink-soft">{status}…</p>}
      </div>

      <ol aria-label="Design stages" className="rounded-2xl border border-line bg-paper overflow-hidden divide-y divide-line">
        {DESIGN_STAGES.map(stage => {
          const state = stageState(stage, progress);
          return (
            <li key={stage} data-stage={stage} data-state={state} aria-current={state === "current" ? "step" : undefined}
              className={`flex gap-3 px-5 py-4 ${state === "current" ? "bg-paper-deep" : ""}`}>
              <span aria-hidden="true" className={`mt-0.5 shrink-0 ${state === "done" ? "text-sage" : state === "current" ? "text-clay" : "text-ink-soft/50"}`}>
                {state === "done" ? <Check className="w-5 h-5" /> : state === "current" ? <LoaderCircle className="w-5 h-5 motion-safe:animate-spin" /> :
                  state === "skipped" ? <Minus className="w-5 h-5" /> : <span className="block w-5 h-5 rounded-full border border-line" />}
              </span>
              <div>
                <p className={`text-sm font-semibold ${state === "pending" || state === "skipped" ? "text-ink-soft" : "text-ink"}`}>
                  {copy[stage].title}<span className="sr-only"> — {state === "done" ? "complete" : state === "current" ? "in progress" : state === "skipped" ? "not needed" : "upcoming"}</span>
                </p>
                {state === "current" && <div className="text-sm text-ink-soft leading-relaxed mt-2 space-y-1">
                  {copy[stage].lines.map(line => <p key={line}>{line}</p>)}
                </div>}
                {state === "skipped" && <p className="text-xs text-ink-soft mt-1">Not needed for your selection.</p>}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="text-center text-xs text-ink-soft space-y-2">
        {startedAt && <p aria-label="Time elapsed">{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} elapsed</p>}
        {elapsed >= 120 && !complete && <p role="status">Taking a little longer than usual. We’re still waiting for this step to finish.</p>}
        {hasSavedRoom && <p>You can refresh this page to check your room’s progress.</p>}
      </div>
    </section>
  );
}
